import { useMemo, useState } from "react";
import AlarmOnOutlinedIcon from "@mui/icons-material/AlarmOnOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { NavLink } from "react-router-dom";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSIconButton from "views/system-design/DSIconButton";
import DSTextField from "views/system-design/DSTextField";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "views/system-design/Table";
import useClocksRemindersSummary from "components/hooks/useClocksRemindersSummary";
import useGetKlydosByIds from "views/Device/hooks/useGetKlydosByIds";
import { Reminder } from "Types";

const DAY_ABBR: Record<string, string> = {
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
  FRIDAY: "Fri",
  SATURDAY: "Sat",
  SUNDAY: "Sun",
};

const flatCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "surface.card",
  backgroundImage: "none",
  borderRadius: "20px",
} as const;

type FlatRow = {
  clockId: string;
  idf?: string;
  klydoId: string;
  reminder: Reminder;
  volume?: number;
  isMuted?: boolean;
};

function formatDays(days: string[]): string {
  if (!days?.length) return "—";
  if (days.length === 7) return "every day";
  return days.map((d) => DAY_ABBR[d] ?? d).join(", ");
}

function RemindersWidget({ sx }: { sx?: object }) {
  const { data, isLoading, error } = useClocksRemindersSummary();
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("");

  const stats = useMemo(() => {
    if (!data) return null;
    let totalReminders = 0;
    let enabledReminders = 0;
    let mutedKlydos = 0;
    let totalKlydosWithSettings = 0;
    const timeCounts = new Map<string, number>();

    for (const entry of data) {
      for (const reminders of Object.values(entry.clockReminders)) {
        for (const r of reminders) {
          totalReminders++;
          if (r.enabled) enabledReminders++;
          timeCounts.set(r.time, (timeCounts.get(r.time) ?? 0) + 1);
        }
      }
      for (const settings of Object.values(
        entry.reminderKlydosSettings ?? {},
      )) {
        totalKlydosWithSettings++;
        if (settings.isMuted) mutedKlydos++;
      }
    }

    let mostCommonTime = "—";
    let mostCommonCount = 0;
    timeCounts.forEach((count, time) => {
      if (count > mostCommonCount) {
        mostCommonCount = count;
        mostCommonTime = time;
      }
    });

    return {
      clocksWithReminders: data.length,
      totalReminders,
      enabledReminders,
      mutedKlydos,
      totalKlydosWithSettings,
      mostCommonTime,
    };
  }, [data]);

  const flatRows: FlatRow[] = useMemo(() => {
    if (!data) return [];
    const rows: FlatRow[] = [];
    for (const entry of data) {
      for (const [klydoId, reminders] of Object.entries(entry.clockReminders)) {
        const klydoSettings = entry.reminderKlydosSettings?.[klydoId];
        for (const reminder of reminders) {
          rows.push({
            clockId: entry.clockId,
            idf: entry.idf,
            klydoId,
            reminder,
            volume: klydoSettings?.volume,
            isMuted: klydoSettings?.isMuted,
          });
        }
      }
    }
    rows.sort((a, b) => a.reminder.time.localeCompare(b.reminder.time));
    return rows;
  }, [data]);

  const uniqueKlydoIds = useMemo(
    () => Array.from(new Set(flatRows.map((r) => r.klydoId))),
    [flatRows],
  );
  const { klydos } = useGetKlydosByIds(showModal ? uniqueKlydoIds : []);
  const klydoNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const k of klydos) map.set(k.id, k.name);
    return map;
  }, [klydos]);

  const filteredRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return flatRows;
    return flatRows.filter(
      (r) =>
        r.clockId.toLowerCase().includes(needle) ||
        r.idf?.toLowerCase().includes(needle) ||
        r.klydoId.toLowerCase().includes(needle) ||
        klydoNameById.get(r.klydoId)?.toLowerCase().includes(needle) ||
        r.reminder.time.includes(needle),
    );
  }, [flatRows, filter, klydoNameById]);

  const clickable = !!(data && data.length > 0);

  return (
    <>
      <DSCard sx={{ ...flatCardSx, ...(sx ?? {}) }}>
        <DSCardContent
          sx={{ flex: 1, cursor: clickable ? "pointer" : "default" }}
          onClick={() => clickable && setShowModal(true)}
        >
          <DSBox
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
              Reminders Set
            </DSTypography>
            <DSBox
              sx={{
                color: "surface.accentText",
                "& svg": { fontSize: "1.5rem" },
                display: "flex",
              }}
            >
              <AlarmOnOutlinedIcon />
            </DSBox>
          </DSBox>
          {isLoading ? (
            <DSCircularProgress size={20} />
          ) : error ? (
            <DSTypography variant="body2" sx={{ color: "error.main" }}>
              Failed to load
            </DSTypography>
          ) : !stats || stats.totalReminders === 0 ? (
            <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
              None set
            </DSTypography>
          ) : (
            <DSBox>
              <DSBox sx={{ typography: "h6", fontFamily: "Gilroy-Bold" }}>
                {stats.totalReminders.toLocaleString()}
                <span
                  style={{
                    marginLeft: 6,
                    fontFamily: "Gilroy-Medium",
                    fontSize: 14,
                    opacity: 0.7,
                  }}
                >
                  across {stats.clocksWithReminders.toLocaleString()} clocks
                </span>
              </DSBox>
              <DSBox
                sx={{
                  mt: 1,
                  fontSize: 12,
                  color: "text.secondary",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <DSBox>
                  {stats.enabledReminders} enabled /{" "}
                  {stats.totalReminders - stats.enabledReminders} off
                </DSBox>
                <DSBox>Most common time: {stats.mostCommonTime}</DSBox>
                {stats.totalKlydosWithSettings > 0 && (
                  <DSBox>
                    {stats.mutedKlydos} of {stats.totalKlydosWithSettings} klydo
                    audio entries muted
                  </DSBox>
                )}
              </DSBox>
            </DSBox>
          )}
        </DSCardContent>
      </DSCard>

      <DSDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: { xxs: "100%", sm: "80vh" },
            maxHeight: { xxs: "100%", sm: "80vh" },
          },
        }}
      >
        <DSDialogTitle>
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <DSTypography variant="h6">
              Reminders ({filteredRows.length}
              {filter && filteredRows.length !== flatRows.length
                ? ` of ${flatRows.length}`
                : ""}
              )
            </DSTypography>
            <DSBox
              sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
            >
              <DSTextField
                size="small"
                placeholder="Filter by clock, klydo, or time"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ flex: 1, maxWidth: 360 }}
              />
              <DSIconButton onClick={() => setShowModal(false)} size="small">
                <CloseIcon />
              </DSIconButton>
            </DSBox>
          </DSBox>
        </DSDialogTitle>
        <DSBox
          sx={{
            px: 2,
            pb: 2,
            flex: 1,
            overflow: "auto",
          }}
        >
          {filteredRows.length === 0 ? (
            <DSBox sx={{ textAlign: "center", p: 4, color: "text.secondary" }}>
              No reminders match the filter
            </DSBox>
          ) : (
            <DSTable size="small">
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>Clock</DSTableCell>
                  <DSTableCell>Klydo</DSTableCell>
                  <DSTableCell>Klydo ID</DSTableCell>
                  <DSTableCell>Slot</DSTableCell>
                  <DSTableCell>Time</DSTableCell>
                  <DSTableCell>Days</DSTableCell>
                  <DSTableCell>Enabled</DSTableCell>
                  <DSTableCell>Repeats × Interval</DSTableCell>
                  <DSTableCell>Volume</DSTableCell>
                  <DSTableCell>Muted</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {filteredRows.map((row, i) => (
                  <DSTableRow
                    key={`${row.clockId}:${row.klydoId}:${row.reminder.slot}:${i}`}
                  >
                    <DSTableCell>
                      <NavLink
                        to="/admin/devices"
                        onClick={() =>
                          localStorage.setItem(
                            "devicessearch",
                            row.idf ?? row.clockId,
                          )
                        }
                        title={row.clockId}
                      >
                        {row.idf ?? row.clockId.substring(0, 8)}
                      </NavLink>
                    </DSTableCell>
                    <DSTableCell>
                      <NavLink
                        to="/admin/klydos"
                        onClick={() =>
                          localStorage.setItem("klydossearch", row.klydoId)
                        }
                        title={row.klydoId}
                      >
                        {klydoNameById.get(row.klydoId) ?? "—"}
                      </NavLink>
                    </DSTableCell>
                    <DSTableCell
                      sx={{ color: "text.secondary", fontFamily: "monospace" }}
                      title={row.klydoId}
                    >
                      {row.klydoId.substring(0, 10)}
                    </DSTableCell>
                    <DSTableCell>{row.reminder.slot}</DSTableCell>
                    <DSTableCell>{row.reminder.time}</DSTableCell>
                    <DSTableCell>{formatDays(row.reminder.days)}</DSTableCell>
                    <DSTableCell>
                      {row.reminder.enabled ? "✓" : "—"}
                    </DSTableCell>
                    <DSTableCell>
                      {row.reminder.repeats > 0
                        ? `${row.reminder.repeats} × ${row.reminder.intervalMinutes}m`
                        : "—"}
                    </DSTableCell>
                    <DSTableCell>{row.volume ?? "—"}</DSTableCell>
                    <DSTableCell>{row.isMuted ? "✓" : "—"}</DSTableCell>
                  </DSTableRow>
                ))}
              </DSTableBody>
            </DSTable>
          )}
        </DSBox>
      </DSDialog>
    </>
  );
}

export default RemindersWidget;
